/**
 * Akkrediteringsnivåer for one årgang (Settings [F2]).
 *
 * Not paged, so no cursor and no `MAX_PAGE_SIZE`: a festival has a handful of badge levels, and
 * the contract says why.
 */
import {
  accreditationLevelDtoSchema,
  accreditationLevelListResponseSchema,
  type AccreditationLevelDto,
  type AccreditationLevelListResponse,
  type CreateAccreditationLevelRequest,
  type UpdateAccreditationLevelRequest,
} from '@tvitvi/shared';
import { queryOptions, useMutation, type UseMutationResult } from '@tanstack/react-query';

import { CACHE } from '@/api/cache-tiers';
import { apiRequest } from '@/api/client';
import { qk } from '@/api/query-keys';

const PATH = '/accreditation-levels';

export function accreditationLevelsQueryOptions(orgId: string, editionId: string) {
  return queryOptions({
    queryKey: qk.accreditationLevelList(orgId, editionId),
    queryFn: ({ signal }): Promise<AccreditationLevelListResponse> =>
      apiRequest({
        path: PATH,
        query: { editionId },
        schema: accreditationLevelListResponseSchema,
        signal,
      }),
    // Reference tier: a badge level is a lookup other pages read, not the content of this one.
    ...CACHE.reference,
  });
}

export function useCreateAccreditationLevel(
  orgId: string,
): UseMutationResult<AccreditationLevelDto, unknown, CreateAccreditationLevelRequest> {
  return useMutation({
    mutationFn: (body: CreateAccreditationLevelRequest) =>
      apiRequest({ path: PATH, method: 'POST', body, schema: accreditationLevelDtoSchema }),
    meta: { invalidates: [qk.accreditationLevels(orgId)] },
  });
}

export interface UpdateAccreditationLevelInput {
  levelId: string;
  body: UpdateAccreditationLevelRequest;
}

/** No `key`: it is minted from the first name and never moves, so a rename cannot detach a copy. */
export function useUpdateAccreditationLevel(
  orgId: string,
): UseMutationResult<AccreditationLevelDto, unknown, UpdateAccreditationLevelInput> {
  return useMutation({
    mutationFn: ({ levelId, body }: UpdateAccreditationLevelInput) =>
      apiRequest({
        path: `${PATH}/${levelId}`,
        method: 'PATCH',
        body,
        schema: accreditationLevelDtoSchema,
      }),
    meta: { invalidates: [qk.accreditationLevels(orgId)] },
  });
}

export function useDeleteAccreditationLevel(
  orgId: string,
): UseMutationResult<void, unknown, string> {
  return useMutation({
    mutationFn: (levelId: string) => apiRequest({ path: `${PATH}/${levelId}`, method: 'DELETE' }),
    /* Hele huset: personene med nivået står i akkrediterings-, person- og frivilliglistene. */
    meta: { invalidates: [qk.org(orgId)] },
  });
}

/** «Angre» på et slettet nivå — personene som hadde det får det tilbake (QA5-D). */
export function useRestoreAccreditationLevel(
  orgId: string,
): UseMutationResult<AccreditationLevelDto, unknown, string> {
  return useMutation({
    mutationFn: (levelId: string) =>
      apiRequest({
        path: `${PATH}/${levelId}/restore`,
        method: 'POST',
        schema: accreditationLevelDtoSchema,
      }),
    meta: { invalidates: [qk.org(orgId)] },
  });
}
